<template>
  <div v-if="show" class="scanner-wrapper">
    <div class="layout">
      <div class="container">
        <div id="canvas-container" class="canvas-container">
          <image-drawer
                  :image="image"
                  :points="points"
                  @input="$emit('input', $event)"
          >
            <template #crop>
              <slot name="crop"></slot>
            </template>
            <template #erase>
              <slot name="erase"></slot>
            </template>
            <template #drag>
              <slot name="drag"></slot>
            </template>
            <template #zoomIn>
              <slot name="zoomIn"></slot>
            </template>
            <template #zoomOut>
              <slot name="zoomOut"></slot>
            </template>
            <template #otherBtn>
              <slot name="otherBtn"></slot>
            </template>
          </image-drawer>
          <receipt-scan-loader
                  v-if="isLoading"
                  :phrases="loadingPhrases"
          ></receipt-scan-loader>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ImageDrawer from './ImageDrawer.vue'
import ReceiptScanLoader from './ReceiptScanLoader.vue'
import { useReceiptStore, useErrorStore } from '@/stores'

export default {
  name: 'receipt-scanner',
  emits: ['input'],
  components: {
    ReceiptScanLoader,
    ImageDrawer
  },
  props: {
    // main properties
    file: {
      type: [File,Blob],
      default: null
    },
    show:Boolean,
    // options
    phrases: {
      type: Array,
      required: true
    },
    cornerDetectionPhrases: {
      type: Array,
      required: true,
    },
    skipCornerDetection: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isLoading: false,
      image: null,
      points: [],
      axiosCancelSource: null,
      retryCount: 0,
      maxRetries: 2,
      apiTimeout: 30000 // 30 seconds timeout
    }
  },
  computed: {
    loadingPhrases() {
      if (this.retryCount > 0) {
        return [
          `Retrying receipt processing... (${this.retryCount}/${this.maxRetries})`,
          'Please wait while we process your receipt',
          'This may take a moment on slower connections'
        ]
      }
      return this.image ? this.phrases : this.cornerDetectionPhrases
    }
  },

  watch: {
    file (file) {
      if (!file) {
        // Reset all state when file is removed
        this.resetScanner()
      } else {
        // Process new file
        this.onFileChanged(file)
      }
    }
  },

  methods: {
    onFileChanged () {
      if (!this.file) return

      // Reset retry count for new file
      this.retryCount = 0
      this.processReceiptImage()
    },
    
    processReceiptImage() {
      this.isLoading = true

      const formData = new FormData()
      formData.append('receipt', this.file)

      this.axiosCancelSource = axios.CancelToken.source()

      if (this.skipCornerDetection) {
        this.fallbackToOriginalImage()
        return
      }
      
      const receiptStore = useReceiptStore()
      const errorStore = useErrorStore()
      
      // Log the request payload for debugging
      console.log('getConvertedImageWithPoints request:', {
        endpoint: 'https://receipt.justsnap.eu/getConvertedImageWithPoints',
        fileSize: this.file.size,
        fileType: this.file.type,
        fileName: this.file.name
      })
      
      // Add timeout and retry logic
      axios.post(`https://receipt.justsnap.eu/getConvertedImageWithPoints`, formData, { 
        cancelToken: this.axiosCancelSource.token,
        timeout: this.apiTimeout 
      })
        .then(resp => {
          console.log('getConvertedImageWithPoints response:', resp.data.response)
          this.isLoading = false
          this.axiosCancelSource = null
          
          if (resp.data.response === 'OK') {
            this.handleSuccessfulResponse(resp.data)
            receiptStore.setReceiptImage(this.file)
          } else {
            console.error('getConvertedImageWithPoints API error:', resp.data)
            this.handleApiFailure({
              message: resp.data.message,
              responseData: resp.data,
              fileInfo: {
                size: this.file.size,
                type: this.file.type,
                name: this.file.name
              }
            }, errorStore)
          }
        })
        .catch(error => {
          console.error('getConvertedImageWithPoints request failed:', error)
          this.handleApiFailure({
            message: error.message,
            errorCode: error.code,
            stack: error.stack,
            fileInfo: {
              size: this.file.size,
              type: this.file.type,
              name: this.file.name
            }
          }, errorStore)
        })
    },
    
    handleSuccessfulResponse(data) {
      try {
        const img = new Image()
        img.addEventListener('load', () => {
          this.points = JSON.parse(data.points)
          this.image = img
        });

        // Converting base64 to blob
        let ImageURL = data.image
        var block = ImageURL.split(';');
        var contentType = block[0].split(':')[1];
        var realData = block[1].split(',')[1];
        var blob = this.b64toBlob(realData, contentType);
        var objectURL = URL.createObjectURL(blob);
        img.src = objectURL
      } catch (error) {
        console.error('Error processing successful response:', error)
        this.handleApiFailure('Failed to process receipt image', { handleError: () => {} })
      }
    },
    
    handleApiFailure(errorData, errorStore) {
      this.isLoading = false
      this.axiosCancelSource = null
      
      // Retry logic
      if (this.retryCount < this.maxRetries) {
        this.retryCount++
        console.log(`Retrying getConvertedImageWithPoints (${this.retryCount}/${this.maxRetries})...`)
        
        // Exponential backoff: 1s, 2s for retries
        const delay = Math.pow(2, this.retryCount - 1) * 1000
        setTimeout(() => {
          this.processReceiptImage()
        }, delay)
        return
      }
      
      // After max retries, fall back to original image
      console.warn(`getConvertedImageWithPoints failed after ${this.maxRetries} retries. Falling back to original image.`)
      
      // Use error store to handle the error with detailed payload info
      errorStore.handleError({
        message: errorData.message || 'Receipt processing failed. Using original image.',
        retryCount: this.retryCount,
        endpoint: 'getConvertedImageWithPoints',
        requestPayload: errorData.fileInfo,
        responseData: errorData.responseData,
        errorCode: errorData.errorCode,
        stack: errorData.stack
      })
      
      // Fall back to showing the original image without corner detection
      this.fallbackToOriginalImage()
    },
    
    fallbackToOriginalImage() {
      this.isLoading = false

      const img = new Image()
      const reader = new FileReader()

      reader.onload = (event) => {
        img.onload = () => {
          this.points = []
          this.image = img
          
          // Still set the receipt image so user can proceed
          const receiptStore = useReceiptStore()
          receiptStore.setReceiptImage(this.file)
        }
        img.src = event.target.result
      }
      reader.readAsDataURL(this.file)
    },
    b64toBlob (b64Data, contentType, sliceSize) {
      contentType = contentType || '';
      sliceSize = sliceSize || 512;

      var byteCharacters = atob(b64Data);
      var byteArrays = [];

      for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        var slice = byteCharacters.slice(offset, offset + sliceSize);

        var byteNumbers = new Array(slice.length);
        for (var i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        var byteArray = new Uint8Array(byteNumbers);

        byteArrays.push(byteArray);
      }

      var blob = new Blob(byteArrays, { type: contentType });
      return blob;
    },
    
    // Method to reset the component state
    resetScanner() {
      this.isLoading = false
      this.image = null
      this.points = []
      this.retryCount = 0
      
      if (this.axiosCancelSource) {
        this.axiosCancelSource.cancel('Reset scanner')
        this.axiosCancelSource = null
      }
    }
  }
}
</script>
